// src/utils/auth-cleanup.js
import config from '@/config'
import { signOut as amplifySignOut } from 'aws-amplify/auth'

/**
 * Remove ALL Cognito/Amplify artifacts from localStorage.
 * Also removes your app keys (e.g., "deposit-frontend") when asked.
 */
export function purgeAuthStorage({ includeAppKeys = true, appKeys = ['deposit-frontend'] } = {}) {
  try {
    const clientId = config.clientId
    const idPool   = config.identityPoolId

    // Snapshot keys first (so we don't mutate while iterating)
    const keys = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k) keys.push(k)
    }

    const toDelete = new Set()

    for (const k of keys) {
      // Everything under Cognito namespace (covers .signInDetails too)
      if (k.startsWith('CognitoIdentityServiceProvider.')) toDelete.add(k)

      // Belts & braces
      if (k.endsWith('.signInDetails')) toDelete.add(k)     // explicitly catch the one you saw
      if (clientId && k.includes(clientId)) toDelete.add(k) // client-scoped artifacts
      if (idPool   && k.includes(idPool))   toDelete.add(k) // identity pool caches

      // Common token/id keys
      if (
        /CognitoIdentityId|identity-id|identityId|federatedInfo|LastAuthUser|accessToken|idToken|tokens|refreshToken/i
          .test(k)
      ) toDelete.add(k)

      // Amplify generic caches
      if (k.startsWith('amplify-') || k.startsWith('aws-amplify-')) toDelete.add(k)

      // App-specific keys
      if (includeAppKeys) {
        for (const appKey of appKeys) {
          if (k === appKey || k.startsWith(appKey)) toDelete.add(k)
        }
      }
    }

    for (const k of toDelete) localStorage.removeItem(k)
  } catch (e) {
    console.warn('[purgeAuthStorage] failed', e)
  }
}

/** Remove cached Cognito Identity Pool identityId/federation blobs. */
export function clearIdentityCache() {
  try {
    const idPool = config.identityPoolId
    if (!idPool) return

    const keys = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k) keys.push(k)
    }

    const toDelete = keys.filter(k =>
      k.includes(idPool) &&
      /CognitoIdentityId|identity-id|identityId|federatedInfo/i.test(k)
    )
    toDelete.forEach(k => localStorage.removeItem(k))
  } catch (e) {
    console.warn('[clearIdentityCache] failed', e)
  }
}

/**
 * Call this once on app boot (TOP of main.js), BEFORE configuring Amplify/Cognito.
 * Keeps app keys; the full sign-out path will nuke them.
 */
export function bootScrub() {
  purgeAuthStorage({ includeAppKeys: false })
  // (We intentionally don't clear sessionStorage here.)
}

/** Optional: call in SignIn.vue mounted() for an extra-clean slate. */
export function ensureCleanSignin() {
  purgeAuthStorage({ includeAppKeys: true })
  try { sessionStorage.clear() } catch (e) {
    console.debug('sessionStorage.clear failed', e)
  }
}

/** Full, hard sign-out: revoke, purge, clear, and redirect to /signin (cache-busted). */
export async function hardSignOut({ reload = true } = {}) {
  try { await amplifySignOut({ global: true }) } catch (e) {
    console.debug('amplifySignOut failed', e)
  }
  clearIdentityCache()
  purgeAuthStorage({ includeAppKeys: true })
  try { sessionStorage.clear() } catch (e) { console.debug('sessionStorage.clear failed', e) }
  if (reload) window.location.replace(`/signin?fl=${Date.now()}`)
}
